import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Typography,
  Tag,
  Button,
  Space,
  Row,
  Col,
  Timeline,
  Statistic,
  Table,
  Badge,
  Spin,
  message,
  Empty,
  Segmented,
  Image
} from 'antd';
import {
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  ShoppingOutlined,
  MessageOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';

const getMainPhoto = (product) => {
  if (!product) return null;
  const photos = product.photos || [];
  const primary = photos.find((ph) => ph.isPrimary) || photos[0];
  return (
    primary?.photoUrl ||
    primary?.originalPhotoUrl ||
    product.photoUrl ||
    product.originalPhotoUrl ||
    null
  );
};
const { Text, Title } = Typography;
const API = 'https://limitverify.ru:5000';

const ClientCard = () => {
  const [client, setClient] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [products, setProducts] = useState([]);
  const [ownershipHistory, setOwnershipHistory] = useState([]);
  const [productFilter, setProductFilter] = useState('current');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const token = localStorage.getItem('token');
  const fromTicketId = location.state?.fromTicketId;

  const goBack = () => {
    if (fromTicketId) {
      navigate(`/tickets/${fromTicketId}`);
      return;
    }
    navigate('/clients');
  };

  const authHeaders = { Authorization: `Bearer ${token}` };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const loadAll = useCallback(async () => {
    if (!id || !token) return;
    setLoading(true);
    try {
      const [clientRes, productsRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/api/clients/${id}`, { headers: authHeaders }),
        axios.get(`${API}/api/product-history/client/${id}/products?status=all`, { headers: authHeaders }),
        axios.get(`${API}/api/tickets`, {
          headers: authHeaders,
          params: { client_id: id, page: 1, limit: 100, status: 'all' }
        }).catch(() => ({ data: { tickets: [] } }))
      ]);

      setClient(clientRes.data.client || clientRes.data);

      const owners = productsRes.data?.data || productsRes.data?.products || [];
      const mapped = owners.map((owner) => {
        const p = owner.product || owner;
        return {
          ownerId: owner.id || `${p.id}-${owner.status || 'current'}`,
          id: p.id,
          name: p.name || 'Без названия',
          serial: p.serialNumber || p.serial_number || '—',
          status: owner.status || p.status || 'current',
          productStatus: p.status || null,
          photoUrl: getMainPhoto(p),
          created_at: owner.ownershipStartDate || p.createdAt || p.created_at
        };
      });
      setOwnershipHistory(mapped);

      const byId = new Map();
      mapped.forEach((item) => {
        if (item.status !== 'current') return;
        byId.set(item.id, {
          ...item,
          displayStatus: item.productStatus === 'АННУЛИРОВАНО' ? 'cancelled' : 'current'
        });
      });
      mapped.forEach((item) => {
        if (item.status !== 'previous') return;
        if (byId.has(item.id)) return;
        byId.set(item.id, {
          ...item,
          displayStatus: item.productStatus === 'АННУЛИРОВАНО' ? 'cancelled' : 'previous'
        });
      });
      setProducts(Array.from(byId.values()));

      setTickets(ticketsRes.data?.tickets || []);
    } catch (error) {
      console.error('Ошибка загрузки карточки клиента:', error);
      if (error.response?.status === 404) {
        setClient(null);
      } else {
        message.error('Не удалось загрузить карточку клиента');
      }
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const getStatusConfig = (isActive) =>
    isActive
      ? { color: 'green', text: 'Активен' }
      : { color: 'red', text: 'Неактивен' };

  const getTicketStatusConfig = (status) => {
    const configs = {
      new: { color: 'red', text: 'Новый' },
      in_progress: { color: 'blue', text: 'В работе' },
      closed: { color: 'green', text: 'Закрыт' },
      cancelled: { color: 'gray', text: 'Отменен' }
    };
    return configs[status] || { color: 'default', text: status || '—' };
  };

  const getProductStatusConfig = (status) => {
    const configs = {
      current: { color: 'green', text: 'Активно' },
      previous: { color: 'default', text: 'Архив' },
      cancelled: { color: 'red', text: 'Аннулировано' },
      active: { color: 'green', text: 'Активно' },
      archived: { color: 'default', text: 'Архив' },
      verified: { color: 'green', text: 'Проверено' },
      transferred: { color: 'blue', text: 'Передано' }
    };
    return configs[status] || { color: 'default', text: status || '—' };
  };

  const activityItems = [];
  if (client?.created_at) {
    activityItems.push({
      key: 'reg',
      date: client.created_at,
      action: 'Регистрация в системе',
      description: 'Клиент зарегистрирован'
    });
  }
  ownershipHistory.forEach((p) => {
    if (p.created_at) {
      activityItems.push({
        key: `p-${p.ownerId}`,
        date: p.created_at,
        action: p.status === 'previous' ? 'Изделие передано' : 'Изделие',
        description: `${p.name} (${p.serial})`
      });
    }
  });
  tickets.forEach((t) => {
    if (t.created_at) {
      activityItems.push({
        key: `t-${t.id}`,
        date: t.created_at,
        action: 'Тикет',
        description: t.subject
      });
    }
  });
  activityItems.sort((a, b) => new Date(b.date) - new Date(a.date));

  const ticketColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Тема', dataIndex: 'subject', key: 'subject', ellipsis: true },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => {
        const config = getTicketStatusConfig(status);
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: 'Приоритет',
      dataIndex: 'priority',
      key: 'priority',
      width: 90,
      render: (priority) => (
        <Badge
          count={priority}
          style={{
            backgroundColor:
              priority <= 2 ? '#ff4d4f' : priority <= 3 ? '#faad14' : '#52c41a'
          }}
        />
      )
    },
    {
      title: 'Дата',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (date) => formatDate(date)
    }
  ];

  const productColumns = [
    {
      title: 'Фото',
      dataIndex: 'photoUrl',
      key: 'photo',
      width: 72,
      render: (url, record) =>
        url ? (
          <div onClick={(e) => e.stopPropagation()}>
            <Image
              src={url}
              alt={record.name}
              width={56}
              height={56}
              style={{ objectFit: 'cover', borderRadius: 6 }}
              preview={{ mask: 'Просмотр' }}
            />
          </div>
        ) : (
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 6,
              background: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#bbb',
              fontSize: 11
            }}
          >
            нет
          </div>
        )
    },
    { title: 'Название', dataIndex: 'name', key: 'name' },
    { title: 'Серийный номер', dataIndex: 'serial', key: 'serial' },
    {
      title: 'Статус',
      dataIndex: 'displayStatus',
      key: 'status',
      render: (status) => {
        const config = getProductStatusConfig(status);
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: 'Дата',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => formatDate(date)
    }
  ];

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" tip="Загрузка карточки клиента..." />
      </div>
    );
  }

  if (!client) {
    return (
      <div style={{ padding: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={goBack} style={{ marginBottom: 16 }}>
          {fromTicketId ? 'Назад в тикет' : 'Назад'}
        </Button>
        <Empty description="Клиент не найден" />
      </div>
    );
  }

  const statusConfig = getStatusConfig(client.is_active !== false);
  const telegram =
    client.telegram_nickname
      ? `@${String(client.telegram_nickname).replace(/^@/, '')}`
      : client.telegram_id
        ? `ID: ${client.telegram_id}`
        : '—';

  const productStats = {
    total: products.length,
    current: products.filter((p) => p.displayStatus === 'current').length,
    previous: products.filter((p) => p.displayStatus === 'previous').length,
    cancelled: products.filter((p) => p.displayStatus === 'cancelled').length
  };
  const filteredProducts =
    productFilter === 'all'
      ? products
      : products.filter((p) => p.displayStatus === productFilter);

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={goBack}
          style={{ marginRight: '16px' }}
        >
          {fromTicketId ? 'Назад в тикет' : 'Назад к списку'}
        </Button>
        <Title level={2} style={{ margin: 0, display: 'inline' }}>
          {client.first_name} {client.last_name}
        </Title>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={16}>
          <Card title="Личная информация" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text strong>Имя:</Text>
                    <br />
                    <Text>
                      {client.first_name} {client.last_name}
                    </Text>
                  </div>
                  <div>
                    <Text strong>Email:</Text>
                    <br />
                    <Space>
                      <MailOutlined />
                      <Text>{client.email || '—'}</Text>
                    </Space>
                  </div>
                  <div>
                    <Text strong>Телефон:</Text>
                    <br />
                    <Space>
                      <PhoneOutlined />
                      <Text>{client.phone || '—'}</Text>
                    </Space>
                  </div>
                </Space>
              </Col>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text strong>Telegram:</Text>
                    <br />
                    <Text>{telegram}</Text>
                  </div>
                  <div>
                    <Text strong>Статус:</Text>
                    <br />
                    <Tag color={statusConfig.color}>{statusConfig.text}</Tag>
                  </div>
                  <div>
                    <Text strong>Регистрация:</Text>
                    <br />
                    <Space>
                      <CalendarOutlined />
                      <Text>{formatDate(client.created_at)}</Text>
                    </Space>
                  </div>
                  {client.comment && (
                    <div>
                      <Text strong>Комментарий:</Text>
                      <br />
                      <Text type="secondary">{client.comment}</Text>
                    </div>
                  )}
                </Space>
              </Col>
            </Row>
          </Card>

          <Card title="Тикеты клиента" style={{ marginBottom: 16 }}>
            <Table
              dataSource={tickets}
              columns={ticketColumns}
              rowKey="id"
              pagination={false}
              size="small"
              locale={{ emptyText: 'Тикетов нет' }}
              onRow={(record) => ({
                onClick: () => navigate(`/tickets/${record.id}`),
                style: { cursor: 'pointer' }
              })}
            />
          </Card>

          <Card
            title="Изделия клиента"
            extra={
              <Segmented
                size="small"
                value={productFilter}
                onChange={setProductFilter}
                options={[
                  { label: 'Все', value: 'all' },
                  { label: 'Активные', value: 'current' },
                  { label: 'Архив', value: 'previous' },
                  { label: 'Аннулир.', value: 'cancelled' }
                ]}
              />
            }
          >
            <Table
              dataSource={filteredProducts}
              columns={productColumns}
              rowKey={(row) => row.ownerId || row.id}
              pagination={false}
              size="small"
              locale={{ emptyText: 'Изделий нет' }}
              onRow={(record) => ({
                onClick: () => navigate(`/products/${record.id}`, {
                  state: fromTicketId ? { fromTicketId } : undefined
                }),
                style: { cursor: 'pointer' }
              })}
            />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Статистика" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div
                  onClick={() => setProductFilter('all')}
                  style={{ cursor: 'pointer' }}
                >
                  <Statistic title="Всего было" value={productStats.total} />
                </div>
              </Col>
              <Col span={12}>
                <div
                  onClick={() => setProductFilter('current')}
                  style={{ cursor: 'pointer' }}
                >
                  <Statistic
                    title="Активных"
                    value={productStats.current}
                    prefix={<ShoppingOutlined />}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div
                  onClick={() => setProductFilter('previous')}
                  style={{ cursor: 'pointer' }}
                >
                  <Statistic title="В архиве" value={productStats.previous} />
                </div>
              </Col>
              <Col span={12}>
                <div
                  onClick={() => setProductFilter('cancelled')}
                  style={{ cursor: 'pointer' }}
                >
                  <Statistic title="Аннулировано" value={productStats.cancelled} />
                </div>
              </Col>
              <Col span={24}>
                <Statistic
                  title="Тикетов"
                  value={tickets.length}
                  prefix={<MessageOutlined />}
                />
              </Col>
            </Row>
          </Card>

          <Card title="История активности">
            {activityItems.length === 0 ? (
              <Empty description="Нет событий" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Timeline
                items={activityItems.slice(0, 15).map((item) => ({
                  children: (
                    <div>
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{item.action}</div>
                      <div style={{ color: '#666', fontSize: 12, marginBottom: 4 }}>
                        {formatDate(item.date)}
                      </div>
                      <div style={{ fontSize: 12 }}>{item.description}</div>
                    </div>
                  )
                }))}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ClientCard;
